/**
 * Pure billing helpers for the paywall and account screens. Prices come from
 * `GET /api/payments/plans` (Paddle is the only copy of the catalog), so nothing
 * here hard-codes an amount or a trial length.
 */

/** One plan on sale, as `GET /api/payments/plans` returns it. */
export interface Plan {
  plan: string;
  /** Minor units, as Paddle stores them (1499 = $14.99). */
  amount_minor: number;
  currency: string;
  interval: 'day' | 'week' | 'month' | 'year' | string;
  interval_count: number;
  trial_days: number;
}

const SHORT_INTERVAL: Record<string, string> = { day: 'day', week: 'wk', month: 'mo', year: 'yr' };

export const money = (plan: Plan): string => {
  const format = new Intl.NumberFormat('en-US', { style: 'currency', currency: plan.currency });
  const digits = format.resolvedOptions().maximumFractionDigits ?? 2;
  return format.format(plan.amount_minor / 10 ** digits);
};

/** "$14.99/month", or "$39.99 every 3 months". */
export const priceLabel = (plan: Plan): string =>
  plan.interval_count > 1
    ? `${money(plan)} every ${plan.interval_count} ${plan.interval}s`
    : `${money(plan)}/${plan.interval}`;

/** "$14.99/mo" — for tight rows. */
export const shortPriceLabel = (plan: Plan): string =>
  `${money(plan)}/${SHORT_INTERVAL[plan.interval] ?? plan.interval}`;

/** "3 days", or null when the plan has no trial. */
export const trialLength = (days: number): string | null =>
  days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}` : null;

/** "Start 3-day free trial", or null when the plan has no trial. */
export const trialCta = (days: number): string | null => (days > 0 ? `Start ${days}-day free trial` : null);

/**
 * What the one billing button should do for this user.
 *
 * - `manage` — subscribed: open the Paddle portal.
 * - `fixPayment` — a Paddle payment failed (no grace, so access is off). Paddle is
 *   still retrying that subscription, so a second checkout would double-bill: send
 *   them to the portal to update the card instead.
 * - `fixInStore` — same, but the store bills them; only the store can fix it.
 * - `trial` / `subscribe` — first time vs. returning (one trial per person).
 */
export type BillingAction = 'manage' | 'fixPayment' | 'fixInStore' | 'trial' | 'subscribe';

export const billingAction = (user: {
  is_subscribed: boolean;
  subscription_status: string | null;
  subscription_provider?: string | null;
}): BillingAction => {
  if (user.is_subscribed) return 'manage';
  if (user.subscription_status === 'past_due' || user.subscription_status === 'paused') {
    return user.subscription_provider === 'paddle' ? 'fixPayment' : 'fixInStore';
  }
  return user.subscription_status ? 'subscribe' : 'trial';
};
